import { useMutation, useQuery } from "@tanstack/react-query";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { consolidationContract } from "@common/contracts/consolidation.contract";
import {
    CreateEliminatingEntryBody,
    EliminatingEntry,
    UpdateEliminatingEntryBody,
} from "@common/schemas/domains/accounting/eliminatingEntry.schema";
import { queryClient } from "queryClient";
import { getConsolidationErrorMessage } from "./consolidation.api";
import { invalidateConsolidatedFinancialReports } from "./consolidationCache.helpers";

const eliminatingEntryClient = getBackendAPIClient(consolidationContract);

const eliminatingEntryQueryKeys = {
    all: ["eliminating-entries"] as const,
    list: (workspaceId: string, groupId: string) =>
        [...eliminatingEntryQueryKeys.all, workspaceId, groupId] as const,
};

interface GroupMutationParams {
    workspaceId: string;
    groupId: string;
}

function invalidateEliminatingEntries({
    workspaceId,
    groupId,
}: GroupMutationParams) {
    void queryClient.invalidateQueries({
        queryKey: eliminatingEntryQueryKeys.list(workspaceId, groupId),
    });
    invalidateConsolidatedFinancialReports(queryClient);
}

interface MutationCallbacks {
    onSuccess?: () => void;
    onError?: (message: string) => void;
}

interface ListEliminatingEntriesParams {
    workspaceId?: string | null;
    groupId?: string | null;
}

async function listEliminatingEntries({
    workspaceId,
    groupId,
}: GroupMutationParams): Promise<EliminatingEntry[]> {
    const result = await eliminatingEntryClient.listEliminatingEntries({
        params: { workspaceId, groupId },
    });
    if (result.status !== 200) {
        throw result;
    }
    return result.body.eliminatingEntries;
}

export function useListEliminatingEntriesQuery({
    workspaceId,
    groupId,
}: ListEliminatingEntriesParams) {
    return useQuery({
        queryKey: eliminatingEntryQueryKeys.list(
            workspaceId ?? "",
            groupId ?? "",
        ),
        queryFn: () =>
            listEliminatingEntries({
                workspaceId: workspaceId ?? "",
                groupId: groupId ?? "",
            }),
        enabled: Boolean(workspaceId && groupId),
    });
}

export function useCreateEliminatingEntryMutation(
    { workspaceId, groupId }: GroupMutationParams,
    callbacks?: MutationCallbacks,
) {
    return useMutation({
        mutationFn: async (
            body: CreateEliminatingEntryBody,
        ): Promise<EliminatingEntry> => {
            const result = await eliminatingEntryClient.createEliminatingEntry({
                params: { workspaceId, groupId },
                body,
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (result.status !== 201) {
                throw result;
            }
            return result.body.eliminatingEntry;
        },
        onSuccess: () => {
            invalidateEliminatingEntries({ workspaceId, groupId });
            callbacks?.onSuccess?.();
        },
        onError: (error) =>
            callbacks?.onError?.(
                getConsolidationErrorMessage(
                    error,
                    "Failed to create eliminating entry",
                ),
            ),
    });
}

interface UpdateEliminatingEntryVariables {
    id: string;
    body: UpdateEliminatingEntryBody;
}

export function useUpdateEliminatingEntryMutation(
    { workspaceId, groupId }: GroupMutationParams,
    callbacks?: MutationCallbacks,
) {
    return useMutation({
        mutationFn: async ({
            id,
            body,
        }: UpdateEliminatingEntryVariables): Promise<EliminatingEntry> => {
            const result = await eliminatingEntryClient.updateEliminatingEntry({
                params: { workspaceId, groupId, id },
                body,
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (result.status !== 200) {
                throw result;
            }
            return result.body.eliminatingEntry;
        },
        onSuccess: () => {
            invalidateEliminatingEntries({ workspaceId, groupId });
            callbacks?.onSuccess?.();
        },
        onError: (error) =>
            callbacks?.onError?.(
                getConsolidationErrorMessage(
                    error,
                    "Failed to update eliminating entry",
                ),
            ),
    });
}

export function useDeleteEliminatingEntryMutation(
    { workspaceId, groupId }: GroupMutationParams,
    callbacks?: MutationCallbacks,
) {
    return useMutation({
        mutationFn: async (id: string): Promise<void> => {
            const result = await eliminatingEntryClient.deleteEliminatingEntry({
                params: { workspaceId, groupId, id },
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (result.status !== 200) {
                throw result;
            }
        },
        onSuccess: () => {
            invalidateEliminatingEntries({ workspaceId, groupId });
            callbacks?.onSuccess?.();
        },
        onError: (error) =>
            callbacks?.onError?.(
                getConsolidationErrorMessage(
                    error,
                    "Failed to delete eliminating entry",
                ),
            ),
    });
}
